import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  AppBar,
  Toolbar,
  Button,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { gamoraApi } from '../services/api';
import Logo from './Logo';

const SharedSessionView: React.FC = () => {
  const { shareToken } = useParams<{ shareToken: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionData, setSessionData] = useState<{
    session_id: string;
    session_name: string;
    created_at: string | null;
    messages: Array<{
      id: string;
      message: string;
      sender: string;
      timestamp: string | null;
      metadata: any;
    }>;
  } | null>(null);

  useEffect(() => {
    const loadSharedSession = async () => {
      if (!shareToken) {
        setError('Share token is required');
        setLoading(false);
        return;
      }

      try {
        const data = await gamoraApi.getSharedSession(shareToken);
        setSessionData(data);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to load shared session');
      } finally {
        setLoading(false);
      }
    };

    loadSharedSession();
  }, [shareToken]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !sessionData) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          {error || 'Shared session not found'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <AppBar position="static" sx={{ bgcolor: '#fff', color: '#222', boxShadow: 'none', borderBottom: '1px solid #eee' }}>
        <Toolbar sx={{ minHeight: 64, px: 3 }}>
          <IconButton onClick={() => navigate('/')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
            <Logo size={24} />
            <Typography variant="h6" component="div" sx={{ color: '#222', fontWeight: 600 }}>
              Gamura AI - Shared Chat
            </Typography>
          </Box>
          <Button
            startIcon={<ShareIcon />}
            variant="outlined"
            onClick={() => {
              navigator.clipboard.writeText(window.location.href);
            }}
          >
            Copy Link
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ maxWidth: '900px', mx: 'auto', p: 3 }}>
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
            {sessionData.session_name}
          </Typography>
          {sessionData.created_at && (
            <Typography variant="body2" sx={{ color: '#666' }}>
              Created: {new Date(sessionData.created_at).toLocaleString()}
            </Typography>
          )}
        </Paper>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {sessionData.messages.map((message) => (
            <Paper
              key={message.id}
              sx={{
                p: 2,
                bgcolor: message.sender === 'user' ? '#e3f2fd' : '#fff',
                borderLeft: message.sender === 'user' ? '4px solid #1976d2' : '4px solid #4caf50',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333' }}>
                  {message.sender === 'user' ? 'You' : 'Assistant'}
                </Typography>
                {message.timestamp && (
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    {new Date(message.timestamp).toLocaleString()}
                  </Typography>
                )}
              </Box>
              {message.sender === 'assistant' ? (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm, remarkBreaks]}
                  components={{
                    p: ({ children }) => <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.6 }}>{children}</Typography>,
                    h1: ({ children }) => <Typography variant="h5" sx={{ mb: 1, mt: 2 }}>{children}</Typography>,
                    h2: ({ children }) => <Typography variant="h6" sx={{ mb: 1, mt: 2 }}>{children}</Typography>,
                    h3: ({ children }) => <Typography variant="subtitle1" sx={{ mb: 1, mt: 1 }}>{children}</Typography>,
                    ul: ({ children }) => <Box component="ul" sx={{ pl: 2, mb: 1 }}>{children}</Box>,
                    ol: ({ children }) => <Box component="ol" sx={{ pl: 2, mb: 1 }}>{children}</Box>,
                    li: ({ children }) => <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>{children}</Typography>,
                    code: ({ children }) => (
                      <Box
                        component="code"
                        sx={{
                          bgcolor: '#f5f5f5',
                          px: 0.5,
                          py: 0.25,
                          borderRadius: 0.5,
                          fontSize: '0.875em',
                          fontFamily: 'monospace',
                        }}
                      >
                        {children}
                      </Box>
                    ),
                    blockquote: ({ children }) => (
                      <Box
                        sx={{
                          borderLeft: '3px solid #ccc',
                          pl: 2,
                          ml: 1,
                          my: 1,
                          fontStyle: 'italic',
                          color: '#666',
                        }}
                      >
                        {children}
                      </Box>
                    ),
                  }}
                >
                  {message.message}
                </ReactMarkdown>
              ) : (
                <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                  {message.message}
                </Typography>
              )}
            </Paper>
          ))}
        </Box>

        <Box sx={{ mt: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ color: '#1565c0', textAlign: 'center' }}>
            This is a shared chat session. To start your own conversation,{' '}
            <Button
              variant="text"
              size="small"
              onClick={() => navigate('/')}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              sign in or create an account
            </Button>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default SharedSessionView;

